/**
 * Экран «Программа» (раздел 8).
 *
 * Появился в ответ на просьбу «дай выбирать тренировки». До него единственной
 * дверью в зал был экран «Сегодня»: он показывает ровно одну тренировку — ту,
 * которую назначил сервер. Человек, решивший в среду сделать день ног из
 * пятницы, упирался в стену.
 *
 * Здесь виден весь мезоцикл, и начать можно ЛЮБУЮ тренировку: будущую,
 * пропущенную, уже завершённую. Приложение ведёт учёт, а не выдаёт пропуска.
 *
 * Ни одно число тут не вычисляется (принцип №1): недели, даты, статусы,
 * отметки разгрузки и калибровки приходят готовыми из `GET /mesocycle` и
 * `GET /sessions`. Клиент их только группирует, склоняет и подписывает.
 * Ближайшую тренировку тоже называет сервер (`GET /today`): своё правило
 * означало бы, что два экрана зовут «следующими» разные тренировки.
 */

import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ApiError, api } from '../api/client'
import type {
  Flag,
  Mesocycle,
  MuscleMap,
  ProgramSummary,
  RebuildInput,
  Session,
  SessionStatus,
  Today,
} from '../api/types'
import { haptic } from '../auth/telegram'
import BodyMap from '../components/BodyMap'
import { IconCheck, IconChevronDown } from '../components/icons'
import Reschedule from '../components/Reschedule'
import { ConfirmButton, Empty, ErrorBox, Flags, Loader, formatDate } from '../components/ui'
import { useAsync } from '../hooks'
import { modeLabel } from '../labels'

/** Данные экрана: программа, её тренировки и каталог готовых программ. */
interface PlanView {
  /** `null` — мезоцикла нет (сервер ответил 404). */
  meso: Mesocycle | null
  sessions: Session[]
  /** `null` — каталог не прочитался. Это не ошибка экрана, см. `decideRebuild`. */
  programs: ProgramSummary[] | null
  /**
   * Заявленная при сборке цель по снижению жира.
   *
   * Хранится на пользователе, а не в мезоцикле, и в тело пересборки её надо
   * положить руками: не прислал — сервер подставил свой умолчательный `false`
   * и молча снял кардио (раздел 5.7). `null` — прочитать не удалось; тогда
   * читаем повторно перед самой пересборкой.
   */
  fatLossGoal: boolean | null
  /**
   * Тренировка, которую ближайшей считает сервер (`GET /today`).
   *
   * `null` — сервер не ответил либо открытых тренировок не осталось.
   */
  anchorId: number | null
  /**
   * «Сегодня» глазами сервера — нижняя граница и быстрая кнопка переноса.
   *
   * Только с сервера: у него часовой пояс пользователя. `null` — `/today`
   * не ответил, и кнопки «Сегодня» в переносе не будет.
   */
  today: string | null
  /**
   * Схема нагрузки по мышцам. `null` — плана нет (404), `'failed'` — не
   * загрузилась; план при этом показывается целиком.
   */
  muscleMap: MuscleMap | null | 'failed'
}

/** Параметры автоматической сборки первой программы. Считает их сервер. */
const AUTO_REBUILD: RebuildInput = { days_per_week: 4, weeks: 5, mode: 'surplus' }

const STATUS_LABELS: Record<SessionStatus, string> = {
  planned: 'запланирована',
  in_progress: 'идёт',
  completed: 'сделана',
  skipped: 'пропущена',
}

const WEEK_FORMS: [string, string, string] = ['неделя', 'недели', 'недель']
const SESSION_FORMS: [string, string, string] = ['тренировка', 'тренировки', 'тренировок']
const PROGRAM_FORMS: [string, string, string] = ['программа', 'программы', 'программ']

/**
 * Загрузка экрана.
 *
 * Запросы идут параллельно, но отношение к их отказам разное:
 *   - 404 на `/mesocycle` — это не сбой, а факт «программы ещё нет»;
 *   - `/sessions` без мезоцикла отдаёт пустой список, поэтому его отказ
 *     означает настоящую поломку и роняет экран;
 *   - каталог программ нужен не для показа, а чтобы кнопка «Пересобрать» не
 *     превратила выбранную программу в дежурный шаблон (см. `decideRebuild`).
 *     Без него план всё равно показывается — просто без пересборки;
 *   - цель по снижению жира нужна той же кнопке: сервер не наследует её из
 *     старого мезоцикла, и не переданная она молча снимается вместе с кардио.
 *     Не прочиталась — перечитаем перед нажатием, экран из-за неё не падает;
 *   - `/today` отвечает на вопрос «какая тренировка следующая». Ответ берётся
 *     у сервера, а не выводится здесь из дат и статусов: правило у него своё
 *     (сегодняшняя → последняя просроченная → ближайшая будущая,
 *     `planner.next_session`), и свой вариант означал бы, что «Сегодня» и
 *     «Программа» показывают человеку разные «следующие» тренировки;
 *   - схема нагрузки по мышцам необязательна: её отказ план не роняет, а 404
 *     значит «плана нет» — рисовать нечего.
 */
async function loadPlan(): Promise<PlanView> {
  const [meso, sessions, programs, fatLossGoal, today, muscleMap] = await Promise.all([
    api.get<Mesocycle>('/mesocycle').catch(nullOn404),
    api.get<Session[]>('/sessions'),
    api.get<ProgramSummary[]>('/programs').catch(() => null),
    currentFatLossGoal().catch(() => null),
    api.get<Today>('/today').catch(() => null),
    api
      .get<MuscleMap>('/mesocycle/muscle-map')
      .catch((e: unknown) =>
        e instanceof ApiError && e.status === 404 ? null : ('failed' as const),
      ),
  ])
  return {
    meso,
    sessions,
    programs,
    fatLossGoal,
    anchorId: today?.session?.id ?? null,
    today: today?.date ?? null,
    muscleMap,
  }
}

/**
 * Заявленная при сборке цель по снижению жира.
 *
 * В `Mesocycle` её нет: сервер держит её на пользователе и отдаёт вместе с
 * планом кардио. 404 означает «мезоцикла нет» — наследовать нечего; остальные
 * отказы поднимаются вызывающему, чтобы не выдать «цель снята» за факт.
 */
async function currentFatLossGoal(): Promise<boolean | null> {
  const cardio = await api
    .get<{ fat_loss_goal: boolean }>('/mesocycle/cardio')
    .catch(nullOn404)
  return cardio?.fat_loss_goal ?? null
}

/** 404 на `/mesocycle*` — «программы нет», всё остальное — настоящая ошибка. */
function nullOn404(e: unknown): null {
  if (e instanceof ApiError && e.status === 404) return null
  throw e
}

/** «1 тренировка», «3 тренировки», «5 тренировок»: кривое склонение читается как баг. */
function plural(n: number, forms: [string, string, string]): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return forms[2]
  const mod10 = n % 10
  if (mod10 === 1) return forms[0]
  if (mod10 >= 2 && mod10 <= 4) return forms[1]
  return forms[2]
}

function count(n: number, forms: [string, string, string]): string {
  return `${n} ${plural(n, forms)}`
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'Что-то пошло не так'
}

// --------------------------------------------------------------------------- //
// Группировка по неделям
// --------------------------------------------------------------------------- //

interface WeekGroup {
  weekNo: number
  sessions: Session[]
  isDeload: boolean
  isCalibration: boolean
  done: number
}

/**
 * Разложить тренировки по неделям.
 *
 * Сервер отдаёт их уже упорядоченными (week_no, day_no), но группировка идёт
 * через словарь, а не «пока номер тот же»: при любом изменении сортировки на
 * сервере последовательный вариант тихо нарисовал бы две «Недели 3».
 *
 * Отметки разгрузки и калибровки берутся у самих тренировок — их проставил
 * движок. Выводить неделю из `deload_weeks` значило бы дублировать его логику.
 */
function groupByWeek(sessions: Session[]): WeekGroup[] {
  const byWeek = new Map<number, Session[]>()
  for (const s of sessions) {
    const bucket = byWeek.get(s.week_no)
    if (bucket) bucket.push(s)
    else byWeek.set(s.week_no, [s])
  }
  return [...byWeek.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([weekNo, list]) => ({
      weekNo,
      sessions: list,
      isDeload: list.some((s) => s.is_deload),
      isCalibration: list.some((s) => s.is_calibration),
      done: list.filter((s) => s.status === 'completed').length,
    }))
}

/**
 * Запасной ответ на вопрос «какая тренировка следующая».
 *
 * Основной даёт сервер (`GET /today`, поле `anchorId`) — его правило учитывает
 * даты и часовой пояс пользователя. Сюда попадаем, только когда сервер на этот
 * вопрос не ответил: тогда берём начатую, иначе первую незакрытую. Свой отбор
 * по датам здесь не делается — это был бы второй ответ на тот же вопрос, и
 * после первой же пропущенной тренировки он разошёлся бы с «Сегодня».
 */
function nextSession(sessions: Session[]): Session | null {
  return (
    sessions.find((s) => s.status === 'in_progress') ??
    sessions.find((s) => s.status === 'planned') ??
    null
  )
}

// --------------------------------------------------------------------------- //
// Пересборка
// --------------------------------------------------------------------------- //

/**
 * Что произойдёт по кнопке «Пересобрать».
 *
 * `ok: false` — не отказ сервера, а честное «сейчас нельзя»: собрать не то,
 * что было, хуже, чем не собрать ничего.
 */
type RebuildDecision =
  | { ok: true; body: RebuildInput; summary: string }
  | { ok: false; reason: string }

/**
 * Собрать тело запроса из текущих параметров мезоцикла.
 *
 * Хитрость в поле `template_code`: там лежит либо код готовой программы
 * (GZCLP, PPL6), либо код шаблона движка (UL4). Различить их по виду нельзя, а
 * разница принципиальная — готовая программа передаётся полем `program_code` и
 * сама решает, сколько в ней недель и дней; шаблон собирается по
 * `template_code` и `weeks`. Отправить код шаблона как `program_code` — это
 * 422, а не отправить `program_code` вовсе — молча получить вместо выбранной
 * программы дежурный шаблон.
 *
 * Что за план под кодом, говорит сервер (`kind`, `base_template`). Считать
 * шаблон по числу тренировок в неделе здесь нельзя: у плана с доп. занятием
 * под цель тренировок на одну больше, чем дней в шаблоне, и «фулбоди + доп.
 * занятие» пересобрался бы верхом/низом. Сверка с каталогом осталась запасным
 * путём — для ответа, сохранённого до появления этих полей.
 *
 * Доп. занятие под цель в тело НЕ кладётся, хотя оно известно. Названное
 * прямо, оно становится требованием: не из чего собрать — сервер откажет
 * (422). А «Пересобрать» — это «как было», и оно обязано сработать и после
 * того, как цели закрыты; не переданное занятие сервер наследует сам и, если
 * собрать его не из чего, соберёт программу без него и скажет об этом.
 */
function decideRebuild(
  meso: Mesocycle,
  programs: ProgramSummary[] | null,
  sessions: Session[],
  fatLossGoal: boolean | null,
): RebuildDecision {
  const mode = modeLabel(meso.mode)
  // Кардио привязано к цели по жиру, а не к программе: если цель стоит, о её
  // сохранении надо сказать до нажатия — иначе после пересборки человек решит,
  // что кардио пропало.
  const cardio = fatLossGoal === true ? ' Кардио под снижение жира останется.' : ''

  if (meso.kind === 'template' && meso.base_template) {
    const days = daysPerWeek(sessions)
    return {
      ok: true,
      body: withGoal(
        { template_code: meso.base_template, weeks: meso.weeks, mode: meso.mode },
        fatLossGoal,
      ),
      summary:
        `Соберу «${meso.title || meso.template_code}» заново: ${count(meso.weeks, WEEK_FORMS)}` +
        (days === null ? '' : `, ${count(days, SESSION_FORMS)} в неделю`) +
        `, режим — ${mode}. Упражнения и веса подберёт движок.` +
        (meso.goal_day === true ? ' Доп. занятие соберётся из целей, открытых сейчас.' : '') +
        cardio,
    }
  }

  const known = programs?.find((p) => p.code === meso.template_code) ?? null
  if (meso.kind === 'program' && known === null) {
    // Сервер сказал, что это готовая программа, а каталог её не подтвердил:
    // не прочитался или отстал. Пересобрать всё равно можно — число недель и
    // дней готовая программа задаёт сама, сервер их знает; клиенту их просто
    // нечем назвать.
    return {
      ok: true,
      body: withGoal({ program_code: meso.template_code, mode: meso.mode }, fatLossGoal),
      summary:
        `Соберу «${meso.title || meso.template_code}» заново, режим — ${mode}. ` +
        'Число недель и тренировок задаёт сама программа.' +
        cardio,
    }
  }

  if (programs === null) {
    return {
      ok: false,
      reason:
        'Каталог программ не прочитался, а без него не видно, готовая у тебя программа ' +
        'или шаблон движка. Пересобрать вслепую — это риск получить не то, что было. ' +
        'Обнови экран или выбери программу заново.',
    }
  }

  const program = programs.find((p) => p.code === meso.template_code)
  if (program) {
    // Число недель и дней у готовой программы своё: сервер всё равно подставит
    // его вместо присланного. Шлём только то, на что он смотрит.
    return {
      ok: true,
      body: withGoal({ program_code: program.code, mode: meso.mode }, fatLossGoal),
      summary:
        `Соберу «${program.name}» заново: ${count(program.weeks, WEEK_FORMS)}, ` +
        `${count(program.days_per_week, SESSION_FORMS)} в неделю, режим — ${mode}.` +
        cardio,
    }
  }

  const days = daysPerWeek(sessions)
  if (days === null) {
    return {
      ok: false,
      reason:
        'В программе нет ни одной тренировки — повторять нечего. ' +
        'Выбери готовую программу.',
    }
  }
  return {
    ok: true,
    body: withGoal({ days_per_week: days, weeks: meso.weeks, mode: meso.mode }, fatLossGoal),
    summary:
      `Соберу заново по шаблону «${meso.template_code}»: ${count(meso.weeks, WEEK_FORMS)}, ` +
      `${count(days, SESSION_FORMS)} в неделю, режим — ${mode}. ` +
      'Упражнения и веса подберёт движок.' +
      cardio,
  }
}

/**
 * Дописать в тело пересборки цель по снижению жира.
 *
 * Неизвестную цель не выдумываем: поле остаётся незаполненным, и `rebuild`
 * перечитает её перед отправкой. Прислать `false` наугад значило бы снять
 * кардио, которого никто не отменял.
 */
function withGoal(body: RebuildInput, goal: boolean | null): RebuildInput {
  return goal === null ? body : { ...body, fat_loss_goal: goal }
}

/**
 * Находки движка, приехавшие вместе с переходом на экран.
 *
 * Состояние перехода — чужие данные: его мог положить другой экран или
 * прошлая версия приложения. Берём только то, что похоже на список находок,
 * остальное молча считаем пустым.
 */
function carriedFlags(state: unknown): Flag[] {
  if (typeof state !== 'object' || state === null) return []
  const flags = (state as { buildFlags?: unknown }).buildFlags
  if (!Array.isArray(flags)) return []
  return flags.filter(
    (f): f is Flag =>
      typeof f === 'object' &&
      f !== null &&
      typeof (f as Flag).code === 'string' &&
      typeof (f as Flag).message === 'string' &&
      typeof (f as Flag).severity === 'string',
  )
}

/**
 * Сколько тренировок в неделе.
 *
 * Это не расчёт (принцип №1), а пересчёт присланных строк: номер дня в неделе
 * проставил сервер, и число различных `day_no` — ровно тот параметр, которым
 * план собирали. В самом мезоцикле его нет, а пересборка без него превратила
 * бы трёхдневную программу в дежурную четырёхдневную.
 */
function daysPerWeek(sessions: Session[]): number | null {
  const days = new Set(sessions.map((s) => s.day_no))
  return days.size === 0 ? null : days.size
}

// --------------------------------------------------------------------------- //
// Куски экрана
// --------------------------------------------------------------------------- //

/**
 * Карточка программы: что собрано и чем это можно заменить.
 *
 * Код программы показывается всегда, даже когда есть человеческое название:
 * именно код виден в ответах сервера и в разговоре с ботом, и прятать его
 * значит оставить человека без общего с приложением словаря.
 */
function ProgramCard({
  meso,
  programs,
  busy,
  decision,
  buildFlags,
  onChange,
  onRebuild,
}: {
  meso: Mesocycle
  programs: ProgramSummary[] | null
  busy: boolean
  decision: RebuildDecision
  /** Отчёт движка о последней сборке — `GET /mesocycle` его не отдаёт. */
  buildFlags: Flag[]
  onChange: () => void
  onRebuild: (body: RebuildInput) => void
}) {
  const program = programs?.find((p) => p.code === meso.template_code) ?? null
  // Имя плана называет сервер: у программы движка код («FB3+G») человеку
  // ничего не говорит. Код остаётся строкой ниже — он общий с ботом словарь.
  const title = program ? program.name : meso.title || meso.template_code
  // Чья программа. Автора знает только каталог; не прочитался — про готовую
  // программу не говорим ничего, кроме кода: приписать её движку значило бы
  // выдать чужую методику за свою.
  const origin = program
    ? `${program.code} · ${program.author}`
    : meso.kind === 'program'
      ? meso.template_code
      : `${meso.template_code} · собирает движок`

  return (
    <>
      <div className="card">
        {/* Режим стоит таблеткой среди фактов, а не справа от названия: имя
            программы движка длинное («Фулбоди + доп. занятие под цель»), и
            рядом с ним таблетку сжимало в две строки. */}
        <div className="plan-title">{title}</div>
        <div className="faint">{origin}</div>

        <div className="plan-facts">
          <span className="pill">режим: {modeLabel(meso.mode)}</span>
          <span className="pill">{count(meso.weeks, WEEK_FORMS)}</span>
          <span className="pill">{count(meso.sessions_total, SESSION_FORMS)}</span>
          {meso.has_calibration_week && <span className="pill accent">калибровочная неделя</span>}
          {meso.deload_weeks.length > 0 && (
            <span className="pill warn">
              разгрузка: {plural(meso.deload_weeks.length, WEEK_FORMS)}{' '}
              {meso.deload_weeks.join(', ')}
            </span>
          )}
        </div>

        <div className="faint" style={{ marginTop: 10 }}>
          Начата {formatDate(meso.started_at)}
          {program && program.uses_percentages && (
            <>
              {' · '}веса считаются процентом от твоего максимума
            </>
          )}
        </div>
      </div>

      {/* Находки движка по программе целиком — сколько прислал, столько и
          показываем: это не второстепенная информация, а причина, по которой
          программу, возможно, пора менять.

          `GET /mesocycle` отдаёт этот список пустым: движок наполняет его
          только в ответе на сборку. Поэтому находки той сборки, что человек
          запустил с этого экрана, держатся в состоянии — иначе «слот под
          вертикальную тягу заполнить нечем» знал бы один сервер. */}
      {meso.flags.length > 0 ? (
        <Flags flags={meso.flags} />
      ) : (
        buildFlags.length > 0 && (
          <>
            <div className="faint" style={{ marginBottom: 6 }}>
              Что движок заметил, когда собирал этот план:
            </div>
            <Flags flags={buildFlags} />
          </>
        )
      )}

      <div className="col" style={{ marginBottom: 12 }}>
        <button className="btn wide" disabled={busy} onClick={onChange}>
          Сменить программу
        </button>

        {/* Пересборка стирает незавершённый план — это необратимо, поэтому
            второе нажатие (ConfirmButton), а не модальное окно. */}
        {decision.ok ? (
          busy ? (
            <button className="btn wide" disabled>
              Пересобираю…
            </button>
          ) : (
            <ConfirmButton
              className="btn wide"
              confirmLabel="Точно пересобрать?"
              onConfirm={() => onRebuild(decision.body)}
            >
              Пересобрать
            </ConfirmButton>
          )
        ) : (
          <div className="faint">{decision.reason}</div>
        )}

        {decision.ok && (
          <div className="faint" style={{ marginTop: -4 }}>
            {decision.summary} Незавершённые тренировки этого мезоцикла пропадут, записанные
            подходы останутся.
          </div>
        )}
      </div>
    </>
  )
}

/**
 * Строка тренировки.
 *
 * Завершённая приглушена и помечена галочкой, но не спрятана и не заблокирована:
 * человек вправе дописать подход, который забыл записать в зале. Кнопки старта
 * у неё нет — сервер такую тренировку начать не даст (409), да и время старта
 * менять не за чем.
 */
function SessionRow({
  session,
  accent,
  busy,
  onOpen,
  onEdit,
  onMove,
}: {
  session: Session
  /** Единственная синяя кнопка на экране — та, к которой человек вернётся. */
  accent: boolean
  busy: boolean
  onOpen: () => void
  onEdit: () => void
  onMove: () => void
}) {
  const done = session.status === 'completed'
  const openLabel = done ? 'Открыть' : session.status === 'in_progress' ? 'Продолжить' : 'Начать'

  return (
    <div className={`card tight${done ? ' dim' : ''}`}>
      <div style={{ minWidth: 0 }}>
        <div className="ex-name">
          {done && (
            <span className="sess-mark">
              <IconCheck size={18} />
            </span>
          )}
          {session.label}
        </div>
        <div className="faint">
          {session.planned_for_date === null ? 'без даты' : formatDate(session.planned_for_date)} ·{' '}
          {STATUS_LABELS[session.status]}
        </div>
      </div>
      <div className="sess-actions">
        <button
          className={accent && !done ? 'btn primary' : 'btn'}
          disabled={busy}
          onClick={onOpen}
        >
          {openLabel}
        </button>
        {/* У завершённой тренировки план не правится — сервер отвечает 409, и
            экран правки честно показывает её только на чтение. Обещать
            кнопкой «Править» то, чего не будет, незачем. */}
        <button className="btn ghost" disabled={busy} onClick={onEdit}>
          {done ? 'Состав' : 'Править'}
        </button>
        {/* У закрытой тренировки дата — это факт о сделанной работе, а не
            план: сервер её перенести не даст (409), и кнопку обещать нечего. */}
        {!done && (
          <button className="btn ghost" disabled={busy} onClick={onMove}>
            Перенести
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Неделя мезоцикла.
 *
 * Свёрнута по умолчанию, кроме текущей. Причина простая: шесть недель по шесть
 * тренировок — это 36 строк подряд, и нужная теряется в списке ровно так же,
 * как терялась без этого экрана.
 */
function WeekBlock({
  group,
  open,
  onToggle,
  children,
}: {
  group: WeekGroup
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div>
      <button className="week-head" aria-expanded={open} onClick={onToggle}>
        <span className="week-title">
          Неделя {group.weekNo}
          {group.isDeload && <span className="pill warn">разгрузка</span>}
          {group.isCalibration && <span className="pill accent">калибровка</span>}
        </span>
        <span className="week-meta">
          <span className="faint">
            {count(group.sessions.length, SESSION_FORMS)}
            {group.done > 0 && ` · ${group.done} сделано`}
          </span>
          <span className="week-chevron">
            <IconChevronDown size={18} />
          </span>
        </span>
      </button>
      {open && children}
    </div>
  )
}

const WEEK_KIND_LABELS: Record<string, string> = {
  calibration: 'калибровка',
  deload: 'разгрузка',
}

/**
 * Нагрузка по мышцам — схема человека по плану.
 *
 * Свёрнута по умолчанию: главная работа экрана — начать тренировку. Вывод
 * схемы («Много: плечи. Мало: икры.») вместе с номером недели виден и без
 * раскрытия. Неделю можно выбрать: правка тренировки меняет ровно одну неделю,
 * и её эффект виден только на ней.
 */
function MuscleLoadBlock({
  loaded,
  onRetry,
}: {
  loaded: MuscleMap | 'failed'
  onRetry: () => void
}) {
  const [open, setOpen] = useState(false)
  const [local, setLocal] = useState<MuscleMap | null>(null)
  const [pending, setPending] = useState(false)
  const [localError, setLocalError] = useState<unknown>(null)
  //: Номер последнего запроса по неделе. Поздний ответ по старому плану
  //: (пересборка, перенос) или по соседней неделе не должен лечь поверх.
  const generation = useRef(0)

  // План перечитали — выбранная раньше неделя и её ответ больше не правда.
  useEffect(() => {
    generation.current += 1
    setLocal(null)
    setLocalError(null)
    setPending(false)
  }, [loaded])

  if (loaded === 'failed') {
    return (
      <div className="card tight mm-block">
        <div className="muted">
          Схема нагрузки по мышцам не загрузилась — план ниже показан полностью.
        </div>
        <button className="btn wide" style={{ marginTop: 10 }} onClick={onRetry}>
          Повторить
        </button>
      </div>
    )
  }

  const shown = local ?? loaded
  const weeks = loaded.weeks

  function pickWeek(weekNo: number): void {
    const mine = ++generation.current
    setPending(true)
    setLocalError(null)
    api
      .get<MuscleMap>(`/mesocycle/muscle-map?week_no=${weekNo}`)
      .then((m) => {
        if (mine === generation.current) setLocal(m)
      })
      .catch((e: unknown) => {
        if (mine === generation.current) setLocalError(e)
      })
      .finally(() => {
        if (mine === generation.current) setPending(false)
      })
  }

  return (
    <div className="mm-block">
      <button className="week-head" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="week-title">Нагрузка по мышцам</span>
        <span className="week-meta">
          <span className="week-chevron">
            <IconChevronDown size={18} />
          </span>
        </span>
      </button>
      <div className="muted">{shown.headline}</div>

      {open && (
        <>
          {shown.message !== shown.headline && (
            <div className="muted" style={{ marginTop: 8 }}>
              {shown.message}
            </div>
          )}
          {weeks.length > 1 && (
            <div className="mm-weeks" role="group" aria-label="Неделя плана">
              {weeks.map((w) => {
                const on = shown.week_no === w.week_no
                const kind = WEEK_KIND_LABELS[w.kind]
                return (
                  <button
                    key={w.week_no}
                    type="button"
                    className={`chip${on ? ' on' : ''}`}
                    aria-pressed={on}
                    aria-label={`Неделя ${w.week_no}${kind ? `, ${kind}` : ''}`}
                    disabled={pending}
                    onClick={() => pickWeek(w.week_no)}
                  >
                    {w.week_no}
                  </button>
                )
              })}
            </div>
          )}
          {localError !== null && <ErrorBox error={localError} />}
          <div className="card" style={{ marginTop: 8 }}>
            {pending ? <Loader label="Считаю нагрузку…" /> : <BodyMap map={shown} />}
          </div>
        </>
      )}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Экран
// --------------------------------------------------------------------------- //

export default function Plan() {
  const navigate = useNavigate()
  const { data, error, loading, reload } = useAsync<PlanView>(loadPlan, [])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<unknown>(null)
  /**
   * Недели, свёрнутость которых человек изменил руками. Значения по умолчанию
   * тут не хранятся: пересборка меняет номер текущей недели, и запомненный
   * набор открытых номеров после неё показал бы не то.
   */
  const [toggled, setToggled] = useState<Record<number, boolean>>({})
  const location = useLocation()
  /**
   * Что движок сказал о последней сборке. Держится отдельно, потому что
   * `GET /mesocycle` флаги не отдаёт: они есть только в ответе на пересборку,
   * и перезапрос после неё их потерял бы.
   *
   * Если программу взяли на экране программ, находки приезжают оттуда вместе
   * с переходом: ответ на сборку получил тот экран, а показать его некому,
   * кроме этого.
   */
  const [buildFlags, setBuildFlags] = useState<Flag[]>(() => carriedFlags(location.state))

  // Находки показаны — из истории переходов их убираем. Иначе они вернулись
  // бы при каждом обновлении страницы и выдавали бы себя за отчёт о плане,
  // который с тех пор могли править.
  useEffect(() => {
    if (location.state !== null) navigate(location.pathname, { replace: true, state: null })
    // Один раз, на входе: дальше состояние перехода уже пустое.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  /** Тренировка, для которой открыта панель переноса. */
  const [moving, setMoving] = useState<Session | null>(null)
  /**
   * Что сказал движок о последнем переносе: сдвоенные даты, размер сдвига,
   * дата в прошлом. Держится отдельно от `buildFlags` — это находки о другом
   * действии, и смешивать их в одну кучу значит путать человека.
   */
  const [moveFlags, setMoveFlags] = useState<Flag[]>([])

  /** Открыть тренировку: начать (или продолжить) и уйти на экран подходов. */
  async function open(s: Session): Promise<void> {
    if (busy) return
    setBusy(true)
    setProblem(null)
    haptic('medium')
    try {
      // Повторный старт сервер обрабатывает идемпотентно — отдельной ветки для
      // «Продолжить» не нужно. Завершённую он начать не даст (409), поэтому её
      // просто открываем.
      if (s.status !== 'completed') await api.post<Session>(`/sessions/${s.id}/start`)
      navigate(`/workout/${s.id}`)
    } catch (e) {
      // Экран подходов берёт план с сервера и офлайн не работает, поэтому
      // «открыть всё равно» тут не предлагается: это был бы переход в тупик.
      setProblem(e)
    } finally {
      setBusy(false)
    }
  }

  /** Тренировка переехала: список перечитываем, флаги показываем. */
  async function moved(flags: Flag[]): Promise<void> {
    setMoving(null)
    setMoveFlags(flags)
    // Перезапрос, а не подстановка ответа: при сдвиге даты меняются у
    // половины мезоцикла, и собирать новый список руками значит завести
    // второй источник правды о плане.
    await reload()
  }

  async function rebuild(body: RebuildInput): Promise<void> {
    if (busy) return
    setBusy(true)
    setProblem(null)
    setBuildFlags([])
    try {
      let payload = body
      // Цель по снижению жира при загрузке прочитать не удалось. Читаем её
      // сейчас, ДО пересборки: отказ здесь оставит план нетронутым, а молча
      // снятое кардио (раздел 5.7) — нет.
      if (payload.fat_loss_goal === undefined && data?.meso) {
        payload = withGoal(body, await currentFatLossGoal())
      }
      const fresh = await api.post<Mesocycle>('/mesocycle/rebuild', payload)
      // Единственный момент, когда движок рассказывает о незаполненных слотах
      // и перекосе объёма (раздел 5.4). Дальше этот список уже никто не отдаст.
      setBuildFlags(fresh.flags)
      setToggled({})
      await reload()
    } catch (e) {
      setProblem(e)
    } finally {
      setBusy(false)
    }
  }

  // Спиннер только на первой загрузке: перезапрос после пересборки не должен
  // сдёргивать с экрана уже показанный план.
  if (loading && !data) {
    return (
      <div className="screen">
        <Loader />
      </div>
    )
  }

  if (!data) {
    const authDead = error instanceof ApiError && error.isAuth
    return (
      <div className="screen">
        <h1>Программа</h1>
        <ErrorBox error={error} onRetry={() => void reload()} />
        {authDead && (
          <p className="faint">
            Похоже, сессия закончилась. Открой приложение заново из Telegram или попроси у
            бота новую ссылку командой <b>/login</b>.
          </p>
        )}
      </div>
    )
  }

  const problemBox = problem !== null && (
    <div className="card" style={{ borderColor: 'var(--danger)' }}>
      <div style={{ color: 'var(--danger)' }}>{errorText(problem)}</div>
      <button className="btn wide" style={{ marginTop: 12 }} onClick={() => void reload()}>
        Обновить
      </button>
    </div>
  )

  // Мезоцикла нет. Это не ошибка и не пустой список — это состояние «ещё не
  // начали», и объяснить надо не «здесь пусто», а что делать дальше (принцип
  // честности, раздел 2).
  if (data.meso === null) {
    const catalog = data.programs
    return (
      <div className="screen">
        <h1>Программа</h1>
        <Empty
          title="Программы пока нет"
          hint="Без неё нечего показать на «Сегодня» и не из чего считать прогресс: план — это то, откуда берутся веса."
        />
        <div className="col">
          <button className="btn primary wide big" onClick={() => navigate('/programs')}>
            Выбрать программу
          </button>
          <div className="faint" style={{ marginTop: -4 }}>
            Программы движка — фулбоди, верх/низ, жим/тяга/ноги, с доп. занятием под цель
            или без —{' '}
            {catalog && catalog.length > 0
              ? `и ${count(catalog.length, PROGRAM_FORMS)} известных авторов. `
              : 'и готовые программы известных авторов. '}
            Состав каждой видно до выбора.
          </div>

          <button className="btn wide big" disabled={busy} onClick={() => void rebuild(AUTO_REBUILD)}>
            {busy ? 'Собираю программу…' : 'Собрать автоматически'}
          </button>
          <div className="faint" style={{ marginTop: -4 }}>
            {count(AUTO_REBUILD.weeks ?? 0, WEEK_FORMS)},{' '}
            {count(AUTO_REBUILD.days_per_week ?? 0, SESSION_FORMS)} в неделю, режим —{' '}
            {modeLabel(AUTO_REBUILD.mode ?? '')}. Упражнения и веса подберёт движок под доступное
            железо.
          </div>
        </div>
        {problemBox}
      </div>
    )
  }

  const meso = data.meso
  const groups = groupByWeek(data.sessions)
  // Ближайшую тренировку называет сервер; свой перебор по статусам остаётся
  // запасным ответом на случай, когда он промолчал.
  const next =
    data.sessions.find((s) => s.id === data.anchorId) ?? nextSession(data.sessions)
  // Разворачиваем неделю с ближайшей незакрытой тренировкой, а если закрыты все
  // — последнюю: мезоцикл пройден, и смотреть человек будет на его конец.
  const currentWeek = next?.week_no ?? groups[groups.length - 1]?.weekNo ?? null

  return (
    <div className="screen">
      <h1>Программа</h1>

      {/* Перезапрос упал, но прошлый ответ на руках. Показать его молча — значит
          выдать устаревший план за текущий; после пересборки это прямая ложь. */}
      {error !== null && (
        <div className="stale-note">
          Не удалось обновить: {errorText(error)}. На экране — прошлый ответ сервера.
        </div>
      )}

      <ProgramCard
        meso={meso}
        programs={data.programs}
        busy={busy}
        decision={decideRebuild(meso, data.programs, data.sessions, data.fatLossGoal)}
        buildFlags={buildFlags}
        onChange={() => navigate('/programs')}
        onRebuild={(body) => void rebuild(body)}
      />

      {problemBox}

      {/* Схема необязательна: 404 значит «плана нет» (сюда не доходит), отказ
          показывается строкой с повтором, а план ниже — целиком в любом случае. */}
      {data.muscleMap !== null && (
        <MuscleLoadBlock loaded={data.muscleMap} onRetry={() => void reload()} />
      )}

      {groups.length === 0 ? (
        <Empty
          title="В программе нет тренировок"
          hint="Так быть не должно. Пересобери программу или выбери готовую — движок разложит её по неделям."
        />
      ) : (
        <>
          <div className="faint" style={{ margin: '18px 0 0' }}>
            Начать можно любую тренировку — не только сегодняшнюю. Пропустил из-за
            болезни или праздника — перенеси её на другую дату.
          </div>
          {/* Находки движка о переносе: две тренировки в один день, сколько
              всего сдвинулось, дата в прошлом. Показываются как есть. */}
          {moveFlags.length > 0 && <Flags flags={moveFlags} />}
          {groups.map((g) => (
            <WeekBlock
              key={g.weekNo}
              group={g}
              open={toggled[g.weekNo] ?? g.weekNo === currentWeek}
              onToggle={() =>
                setToggled((prev) => ({
                  ...prev,
                  [g.weekNo]: !(prev[g.weekNo] ?? g.weekNo === currentWeek),
                }))
              }
            >
              {g.sessions.map((s) =>
                moving?.id === s.id ? (
                  <Reschedule
                    key={s.id}
                    session={s}
                    today={data.today}
                    onMoved={(result) => void moved(result.flags)}
                    onCancel={() => setMoving(null)}
                  />
                ) : (
                  <SessionRow
                    key={s.id}
                    session={s}
                    accent={next !== null && s.id === next.id}
                    busy={busy}
                    onOpen={() => void open(s)}
                    onEdit={() => navigate(`/plan/${s.id}`)}
                    onMove={() => {
                      setMoveFlags([])
                      setMoving(s)
                    }}
                  />
                ),
              )}
            </WeekBlock>
          ))}
        </>
      )}
    </div>
  )
}
